import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { 
  Code, 
  Copy, 
  Upload, 
  Download, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  FileText
} from 'lucide-react';
import { TemplateContent, zTemplateContent } from '../../../../src/schemas/templateContent';

interface JsonPanelProps {
  content: TemplateContent;
  onChange: (content: TemplateContent) => void;
}

export function JsonPanel({ content, onChange }: JsonPanelProps) {
  const [importJson, setImportJson] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<{ ok: boolean; issues?: string[] } | null>(null);
  const { toast } = useToast();

  // Pretty print the JSON content
  const jsonString = JSON.stringify(content, null, 2);

  // Validate JSON mutation
  const validateJsonMutation = useMutation({
    mutationFn: async (jsonContent: any) => {
      const response = await apiRequest('POST', '/api/templates/validate', {
        content: jsonContent
      });
      return response.json();
    },
    onSuccess: (result) => {
      setValidationResult(result);
    },
    onError: () => {
      setValidationResult({ ok: false, issues: ['Network error during validation'] });
    }
  });

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      toast({
        title: 'Copied to clipboard',
        description: 'Template JSON has been copied to your clipboard.'
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Could not copy to clipboard.',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadJson = () => {
    const dataStr = jsonString;
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template-content.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportJson = () => {
    try {
      const parsedJson = JSON.parse(importJson);
      
      // Validate using Zod schema
      const validation = zTemplateContent.safeParse(parsedJson);
      
      if (validation.success) {
        onChange(validation.data);
        setImportDialogOpen(false);
        setImportJson('');
        setValidationResult(null);
        toast({
          title: 'Import successful',
          description: 'Template content has been imported and validated.'
        });
      } else {
        const issues = validation.error.errors.map(err => 
          `${err.path.join('.')}: ${err.message}`
        );
        setValidationResult({ ok: false, issues });
      }
    } catch (error) {
      setValidationResult({ 
        ok: false, 
        issues: ['Invalid JSON format. Please check your syntax.'] 
      });
    }
  };

  const handleValidateImport = () => {
    try {
      const parsedJson = JSON.parse(importJson);
      validateJsonMutation.mutate(parsedJson);
    } catch (error) {
      setValidationResult({ 
        ok: false, 
        issues: ['Invalid JSON format. Please check your syntax.'] 
      });
    }
  };

  const getValidationIcon = () => {
    if (!validationResult) return null;
    
    if (validationResult.ok) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    } else {
      return <XCircle className="w-4 h-4 text-red-600" />;
    }
  };

  const getValidationBadge = () => {
    if (!validationResult) return null;
    
    return (
      <Badge 
        variant={validationResult.ok ? "default" : "destructive"}
        className="flex items-center space-x-1"
      >
        {getValidationIcon()}
        <span>{validationResult.ok ? 'Valid' : 'Invalid'}</span>
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* JSON Viewer */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Code className="w-5 h-5 text-purple-600" />
              <CardTitle>Template JSON</CardTitle>
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCopyJson}
                data-testid="button-copy-json"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadJson}
                data-testid="button-download-json"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            View and export your template as JSON
          </p>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto max-h-96 overflow-y-auto font-mono">
              <code data-testid="json-content">{jsonString}</code>
            </pre>
            <div className="absolute top-2 right-2">
              <Badge variant="outline" className="text-xs">
                {content.items.length} items
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import JSON */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Upload className="w-5 h-5 text-green-600" />
              <CardTitle>Import JSON</CardTitle>
            </div>
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  data-testid="button-open-import"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Import Template JSON</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">JSON Content</label>
                    <Textarea
                      value={importJson}
                      onChange={(e) => {
                        setImportJson(e.target.value);
                        setValidationResult(null); // Clear validation when content changes
                      }}
                      placeholder="Paste your template JSON here..."
                      className="mt-1 h-64 font-mono text-sm"
                      data-testid="textarea-import-json"
                    />
                  </div>
                  
                  {/* Validation Result */}
                  {validationResult && (
                    <Alert className={validationResult.ok ? 'border-green-200 bg-green-50 dark:bg-green-950/20' : 'border-red-200 bg-red-50 dark:bg-red-950/20'}>
                      <div className="flex items-start space-x-2">
                        {getValidationIcon()}
                        <div className="flex-1">
                          <AlertDescription>
                            {validationResult.ok ? (
                              <span className="text-green-800 dark:text-green-400">
                                JSON is valid and ready to import!
                              </span>
                            ) : (
                              <div className="text-red-800 dark:text-red-400">
                                <div className="font-medium mb-2">Validation errors found:</div>
                                <ul className="list-disc list-inside space-y-1">
                                  {validationResult.issues?.map((issue, index) => (
                                    <li key={index} className="text-sm">{issue}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </AlertDescription>
                        </div>
                      </div>
                    </Alert>
                  )}
                  
                  <div className="flex justify-between">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        onClick={handleValidateImport}
                        disabled={!importJson.trim() || validateJsonMutation.isPending}
                        data-testid="button-validate-import"
                      >
                        {validateJsonMutation.isPending ? (
                          <>Validating...</>
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Validate
                          </>
                        )}
                      </Button>
                      {getValidationBadge()}
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setImportDialogOpen(false);
                          setImportJson('');
                          setValidationResult(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleImportJson}
                        disabled={!importJson.trim() || (validationResult ? !validationResult.ok : false)}
                        data-testid="button-confirm-import"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Import Template
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-sm text-muted-foreground">
            Import template content from JSON with validation
          </p>
        </CardHeader>
        <CardContent>
          <Alert>
            <FileText className="w-4 h-4" />
            <AlertDescription>
              <strong>Import Guidelines:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>JSON must follow the template content schema</li>
                <li>All required fields must be present (metaFields, items, etc.)</li>
                <li>Item types must be valid (section, ynna_actions_review, dropdown, text, textarea, number)</li>
                <li>Scoring only supported for ynna_actions_review items</li>
                <li>Use the validation button to check before importing</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Schema Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Schema Reference</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Use this schema when converting documents to JSON for import
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Required Structure:</h4>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "version": 1,
  "metaFields": [...],
  "tableColumns": [...],
  "items": [...],
  "signoff": [...]
}`}
              </pre>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Item Structure:</h4>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "id": "item_123",              // Unique ID
  "label": "Question text",      // Display text
  "type": "ynna_actions_review", // See types below
  "required": false,             // Optional: Is answer required?
  "actions_enabled": false,      // Optional: Enable actions section?
  "config": {                    // Optional: For dropdown items
    "choices": [
      { "id": "c1", "label": "Option 1", "value": "opt1" }
    ]
  },
  "score": {                     // Optional: For ynna_actions_review only
    "enabled": true,
    "weight": 1,
    "map": { "Yes": 1, "No": 0, "N/A": "ignore" }
  }
}`}
              </pre>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Item Types:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">section</Badge>
                  <span className="text-muted-foreground">Section header (no input)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">ynna_actions_review</Badge>
                  <span className="text-muted-foreground">Yes/No/N/A question with scoring</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">dropdown</Badge>
                  <span className="text-muted-foreground">Dropdown with custom options (no scoring)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">text</Badge>
                  <span className="text-muted-foreground">Single-line text input</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">textarea</Badge>
                  <span className="text-muted-foreground">Multi-line text input</span>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5">number</Badge>
                  <span className="text-muted-foreground">Numeric input</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Dropdown Items:</h4>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "type": "dropdown",
  "config": {
    "choices": [
      { "id": "choice_1", "label": "Good", "value": "good" },
      { "id": "choice_2", "label": "Fair", "value": "fair" },
      { "id": "choice_3", "label": "Poor", "value": "poor" }
    ]
  }
  // Note: Dropdown items do NOT support scoring
}`}
              </pre>
            </div>

            <div>
              <h4 className="font-medium mb-2">Example Item:</h4>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "id": "item_1234567890",
  "label": "Are call bells within reach?",
  "type": "ynna_actions_review",
  "required": true,
  "actions_enabled": true,
  "score": {
    "enabled": true,
    "weight": 1,
    "map": { "Yes": 1, "No": 0, "N/A": "ignore" }
  }
}`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}